import { useState, useEffect, useContext } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { Container } from 'reactstrap'

import { API } from 'API'
import { RequestStatus } from 'components/RequestStatus'
import { UserSearch } from 'components/UserSearch'
import { AppRoutes } from 'constants/appRoutes'
import { UserAccessPermissionsEnum } from 'constants/userAccessPermissionsEnum'
import { RequestToasterContext } from 'containers/Providers'
import { getUserAuthorities, getUserProfile } from 'core/auth'
import { FeatureFlags } from 'domains/featureFlags'
import { User } from 'domains/user'
import { useEffectOnce } from 'hooks/useEffectOnce'
import { StandardErrorResponse } from 'types/APIResponses'
import { Nullable } from 'types/utility'
import { isNotNil } from 'utils/isNotNil'

import { UserInfo } from './components'

const USER_PARAMS = { userId: 'userId' } as const

export const UsersPage = () => {
  const navigate = useNavigate()
  const params = useParams()
  const [searchParams] = useSearchParams()
  const { requestStatusRef } = useContext(RequestToasterContext)

  const [username, setUsername] = useState(params[USER_PARAMS.userId] ?? null)
  const [user, setUser] = useState<Nullable<User>>(null)
  const [isCreateAdminAllowed, setIsCreateAdminAllowed] = useState<boolean | undefined>(false)
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({})
  const [loggedUserFeatureFlags, setLoggedUserFeatureFlags] = useState<FeatureFlags>({})
  const [loggedUserAccessRoles, setLoggedUserAccessRoles] = useState('')
  const loggedUserName = getUserProfile()?.user_name ?? ''

  useEffect(() => {
    if (isNotNil(username)) {
      handleGet()
    } else {
      setUser(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username])

  useEffectOnce(() => {
    setIsCreateAdminAllowed(
      getUserAuthorities().includes(UserAccessPermissionsEnum.SUPPORT_TOOL_CREATE_ADMIN),
    )

    if (loggedUserName) {
      API.getUsersFeatureFlags(loggedUserName, 'WEB').then(setLoggedUserFeatureFlags)
      API.getUser(encodeURIComponent(loggedUserName), true)
        .then(({ user }) => setLoggedUserAccessRoles(user.accessRoles ?? ''))
        .catch(() => setLoggedUserAccessRoles(''))
    }
  })

  const handleGet = () => {
    if (!username) {
      return
    }

    requestStatusRef.current?.startProgress('Getting user info...', 'secondary')
    API.getUser(encodeURIComponent(username), searchParams.get('crossRegion') === 'true')
      .then(({ user, featureFlags }) => {
        setUser(user)
        setFeatureFlags(featureFlags)
        navigate(`${AppRoutes.users}/${user.username}`)
        requestStatusRef.current?.showAlert('Done', 'success')
      })
      .catch((error: StandardErrorResponse) => {
        setUsername(null)
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
  }

  const handleNavigateToCreateAdmin = () => {
    if (isCreateAdminAllowed) {
      navigate(AppRoutes.createAdmin)
    }
  }

  return (
    <Container>
      <RequestStatus />
      <h2>Manage users</h2>
      <UserSearch
        rootPath={AppRoutes.users}
        selectedUser={user?.username ?? null}
        onUserSelected={setUsername}
        customAction={
          <button
            onClick={handleNavigateToCreateAdmin}
            disabled={!isCreateAdminAllowed}
            type="button"
            title={
              !isCreateAdminAllowed
                ? 'You are not allowed to perform this action. Please contact your manager to get permission for this action'
                : ''
            }>
            Create Admin User
          </button>
        }
      />
      {isNotNil(user) && (
        <UserInfo
          user={user}
          featureFlags={featureFlags}
          loggedUserName={loggedUserName}
          loggedUserFeatureFlags={loggedUserFeatureFlags}
          loggedUserAccessRoles={loggedUserAccessRoles}
          getUser={handleGet}
        />
      )}
    </Container>
  )
}
